import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { createBundleObject } from '../lib/helpers/bundle';
import { InputMaybe } from '#types';
import { ContactsContext } from '../context/ContactsContextProvider/ContactsContextProvider';
import {
	getSuggestDocumentCreateModalData,
	SuggestDocumentCreateModalData,
} from '../blocks/SuggestDocumentCreateModal';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend( utc );

type NeedsUpdateFromBundleInput<T extends boolean> = T extends true
	? {
		title: string | null | undefined;
		eventDate: InputMaybe<string | Date> | undefined;
		dueDate: string | Date | null | undefined;
	  }
	: {
		title: string | null | undefined;
		eventDate: InputMaybe<string | Date> | undefined;
	  };

const needsUpdateFromBundleInputTypeguard = <T extends boolean>(
	isInvoice: T,
	bundle:
	| NeedsUpdateFromBundleInput<true>
	| NeedsUpdateFromBundleInput<false>
	| SuggestDocumentCreateModalData<T>
): bundle is
	| NeedsUpdateFromBundleInput<true>
	| SuggestDocumentCreateModalData<T> => isInvoice === true;

type StateWithDueDate = {
	title: string;
	dueDate: string | Date | null | undefined;
	eventDate: string | Date | null | undefined;
};

type StateWithoutDueDate = {
	title: string;
	eventDate: string | Date | null | undefined;
};

export const useUpdateStateFromBundleOrContact = <
	K extends boolean = false,
	T extends K extends true
		? StateWithoutDueDate
		: StateWithDueDate = K extends true ? StateWithoutDueDate : StateWithDueDate
>( {
	/* eslint-disable indent */
	isNewDocument,
	contactId,
	set,
	isInvoice,
}: {
	/* eslint-enable indent */
	isNewDocument: boolean;
	contactId?: string;
	set: React.Dispatch<React.SetStateAction<T>>;
	isInvoice?: K;
} ) => {
	/* we only want to set the value from the cookie or bundle one time on "create" pages, to allow the user to alter
	values and not overwrite them on redraw */
	const [ setValue, setSetValue ] = useState( false );

	// in cased we're coming from a documentEditFollowupActions hook, we need to get the follow up action from the cookie
	const followUpAction = useMemo(
		() => getSuggestDocumentCreateModalData( isInvoice || false ),
		[ isInvoice ]
	);
	// in the case of a single document, we can set the contact from the URL param
	const contactsContext = useContext( ContactsContext );
	const setContact = contactsContext?.set;
	const organizationContactsReady = contactsContext?.organizationContactsReady;
	const organizationContacts = contactsContext?.organizationContacts;
	const organizationContact = useMemo(
		() =>
			organizationContactsReady
				? contactId
					? organizationContacts?.find( ( c ) => c.id === contactId )
					: followUpAction?.contactID
						? organizationContacts?.find( ( c ) => c.id === followUpAction.contactID )
						: undefined
				: undefined,
		[
			contactId,
			followUpAction,
			organizationContacts,
			organizationContactsReady
		]
	);

	const bundle = useMemo(
		() => ( contactId ? createBundleObject( contactId ) : undefined ),
		[ contactId ]
	);

	/* In order to determine if the prop value differs from the internal value, only considering
	date and excluding time, we use this helper function */
	const isSameDate = useCallback(
		(
			date1: string | Date | null | undefined,
			date2: string | Date | null | undefined
		) =>
			// if both values are null then true
			!date1 && !date2
				? true
				: // of both values are dates then compare the dates as strings without time
				  ( date1 &&
						date2 &&
						dayjs( date1 ).local().format( 'YYYY-MM-DD' ) ===
							dayjs( date2 ).local().format( 'YYYY-MM-DD' ) ) ||
				  false,
		[]
	);

	const needsUpdateFromBundle = useCallback(
		(
			state?: T,
			bundleOrFollowUpAction?:
			| NeedsUpdateFromBundleInput<K>
			| SuggestDocumentCreateModalData<K>
		): boolean =>
			bundleOrFollowUpAction
				? needsUpdateFromBundleInputTypeguard(
					isInvoice || false,
					bundleOrFollowUpAction
				  )
					? ( !!bundleOrFollowUpAction.title &&
							state?.title !==
								decodeURIComponent( bundleOrFollowUpAction.title ) ) ||
					  ( bundleOrFollowUpAction &&
							'dueDate' in bundleOrFollowUpAction &&
							!!bundleOrFollowUpAction.dueDate &&
							state &&
							'dueDate' in state &&
							!isSameDate( state?.dueDate, bundleOrFollowUpAction.dueDate ) ) ||
					  ( !!bundleOrFollowUpAction.eventDate &&
							!isSameDate( state?.eventDate, bundleOrFollowUpAction.eventDate ) )
					: ( !!bundleOrFollowUpAction.title &&
							state?.title !==
								decodeURIComponent( bundleOrFollowUpAction.title ) ) ||
					  ( !!bundleOrFollowUpAction.eventDate &&
							!isSameDate( state?.eventDate, bundleOrFollowUpAction.eventDate ) )
				: false,
		[ isInvoice, isSameDate ]
	);

	// Bundle actions for setting state from the bundle cookie
	useEffect( () => {
		if (
			isNewDocument &&
			bundle &&
			organizationContact &&
			setContact &&
			!setValue
		) {
			setContact( organizationContact );
			setSetValue( true );
			set( ( prevState ) =>
				needsUpdateFromBundle( prevState, bundle )
					? {
						...prevState,
						...( bundle.title
							? { title: decodeURIComponent( bundle.title ) }
							: {} ),
						...( bundle.dueDate ? { dueDate: bundle.dueDate } : {} ),
						...( typeof bundle.eventDate !== 'undefined'
							? { eventDate: bundle.eventDate }
							: {} ),
					  }
					: prevState
			);
		}
	}, [
		bundle,
		isNewDocument,
		needsUpdateFromBundle,
		set,
		setContact,
		organizationContact,
		setValue,
	] );

	useEffect( () => {
		if (
			isNewDocument &&
			!bundle &&
			!followUpAction &&
			organizationContact &&
			setContact &&
			!setValue
		) {
			setContact( organizationContact );
			setSetValue( true );
			if ( organizationContact.eventDate ) {
				set( ( prevState ) => ( {
					...prevState,
					eventDate: organizationContact.eventDate,
				} ) );
			}
		}
	}, [
		bundle,
		followUpAction,
		isNewDocument,
		organizationContact,
		set,
		setContact,
		setValue,
	] );

	// update state from the follow up action, if exists
	useEffect( () => {
		if (
			isNewDocument &&
			followUpAction &&
			organizationContact &&
			setContact &&
			!setValue
		) {
			setContact( organizationContact );
			setSetValue( true );
			set( ( prevState ) =>
				needsUpdateFromBundle( prevState, followUpAction )
					? {
						...prevState,
						...( followUpAction.title ? { title: followUpAction.title } : {} ),
						...( 'dueDate' in followUpAction && followUpAction.dueDate
							? { dueDate: followUpAction.dueDate }
							: {} ),
						...( typeof followUpAction.eventDate !== 'undefined'
							? { eventDate: followUpAction.eventDate }
							: {} ),
					  }
					: prevState
			);
		}
	}, [
		followUpAction,
		isNewDocument,
		needsUpdateFromBundle,
		organizationContact,
		set,
		setContact,
		setValue,
	] );

	useEffect( () => {
		return () => {
			setSetValue( false );
		};
	}, [] );
};
